import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import TextField from '../textField';
import { useState } from 'react';
import { useUserContext } from '@/contexts/userContext';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '../errorMessage';
import { useTranslation } from 'react-i18next';
import { validatePassword } from '@/utils';
import { useNotificationContext } from '@/contexts/notificationContext';

type Props = {
  onClose: () => void;
};

const UserProfile = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { user, signout } = useUserContext();
  const apiContext = useApiContext();
  const { toast } = useNotificationContext();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [changed, setChanged] = useState(false);

  const [changePassword, setChangePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [closeAccount, setCloseAccount] = useState(false);
  const [closingAccount, setClosingAccount] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [error, setError] = useState<any>();

  const onFirstNameChange = (v: string) => {
    setFirstName(v);
    setChanged(true);
  };
  const onLastNameChange = (v: string) => {
    setLastName(v);
    setChanged(true);
  };

  const reset = () => {
    setFirstName(user?.firstName);
    setLastName(user?.lastName);
    setChanged(false);
    setErrors({});
  };

  const save = async () => {
    setError(null);
    try {
      setLoading(true);
      await apiContext.updateUserProfile(firstName, lastName);
      setChanged(false);
      toast({ message: t('settings.appConfig.saved') });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitChangePassword = async () => {
    setError(null);
    const errs: any = {};
    let hasError = false;
    if (!password) {
      errs['password'] = 'Required field';
      hasError = true;
    }
    if (!validatePassword(password)) {
      errs['password'] = 'Should be 8 to 100 chars long';
      hasError = true;
    }
    if (!confirmPassword) {
      errs['confirmPassword'] = 'Required field';
      hasError = true;
    }
    if (password !== confirmPassword) {
      errs['confirmPassword'] = 'Passwords do not match';
      hasError = true;
    }
    setErrors(errs);
    if (!hasError) {
      try {
        setLoading(true);
        await apiContext.updatePassword(password);
        setChangePassword(false);
        onClose();
        await signout();
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const submitCloseAccount = async () => {
    try {
      setError(null);
      setClosingAccount(true);
      await apiContext.closeAccount();
      await signout();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setClosingAccount(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <ErrorMessage error={error} />}

      {/* ── Profile section ── */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Profile
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Box sx={{ px: 2.5, py: 2 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="First name"
                value={firstName}
                required
                autoFocus
                error={errors['firstName']}
                maxLength={100}
                onChange={onFirstNameChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Last name"
                value={lastName}
                required
                error={errors['lastName']}
                maxLength={100}
                onChange={onLastNameChange}
              />
            </Grid>
          </Grid>
        </Box>
        {changed && (
          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button variant="text" size="small" onClick={reset} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('common.buttons.reset')}
            </Button>
            <Button
              loading={loading}
              size="small"
              color="secondary"
              variant="contained"
              onClick={save}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
                color: 'text.primary',
              }}
            >
              {t('common.buttons.save')}
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Security section ── */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Security
          </Typography>
        </Box>

        {/* Change password fields */}
        {changePassword && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="New password"
                  value={password}
                  type="password"
                  required
                  autoFocus
                  error={errors['password']}
                  maxLength={100}
                  onChange={setPassword}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  required
                  error={errors['confirmPassword']}
                  maxLength={100}
                  onChange={setConfirmPassword}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Close account confirmation */}
        {closeAccount && (
          <Box sx={{ px: 2.5, py: 2 }}>
            {closingAccount ? (
              <Typography variant="body2" color="text.secondary">
                Closing your account. Please wait, it can take several minutes.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Are you sure you want to close your account? This will delete all your apps and data sources.
              </Typography>
            )}
          </Box>
        )}

        {/* Action bar */}
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {!changePassword && !closeAccount && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setChangePassword(true)}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Change password
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setCloseAccount(true)}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Close Account
              </Button>
            </>
          )}
          {changePassword && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={() => setChangePassword(false)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                size="small"
                color="secondary"
                variant="contained"
                onClick={submitChangePassword}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                  color: 'text.primary',
                }}
              >
                Submit
              </Button>
            </>
          )}
          {closeAccount && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={() => setCloseAccount(false)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                loading={closingAccount}
                size="small"
                color="error"
                variant="contained"
                onClick={submitCloseAccount}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Confirm
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          Close
        </Button>
      </Box>
    </Stack>
  );
};

export default UserProfile;
